import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { DateUtil } from 'src/app/shared/util/DateUtil';
import { CalendarClickEvent } from '../../models/calendar-click-event';
import { CalendarEvent } from '../../models/calendar-event';
import { CalendarEventHandler } from '../../models/calendar-event-handler';
import { WeekDayCollection } from '../../models/week-day-collection';
import { DayIndexUtil } from '../../util/DayIndexUtil';
import { CalendarWeekEvent } from '../../models/calendar-week-event';
import { DayTemplateContext, ICalendarComponent } from '../../interface/calendar';
import { CalendarSettings } from '../../models/calendar-settings';

/** 
 * A week calendar.  
 * For documentation see: {@link ICalendarComponent} 
*/
@Component({
  selector: 'app-calendar-week[date]',
  templateUrl: './calendar-week.component.html',
  styleUrls: ['./calendar-week.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarWeekComponent implements OnInit, ICalendarComponent {

  //#region EXTERNAL
  @Input() set date(value: Date) {
    this._date = value;

    this.dates = this.getDaysInWeek(value);
    this.refreshEvents();
  };
  public get date(): Date {
    return this._date;
  }

  @Input() public eventHandler: CalendarEventHandler;
  @Input() public dayTemplate: TemplateRef<DayTemplateContext>;
  @Input() public settings: CalendarSettings;

  @Output() public eventClick: EventEmitter<CalendarClickEvent> = new EventEmitter();

  //#endregion

  //#region INTERNAL
  @ViewChild("ticker") set tickerElement(element: ElementRef) {
    element.nativeElement.style.height = "0%"
    element.nativeElement.style.top = this.currentTimePositionPercentage + '%'

    this._tickerElement = element.nativeElement;
  };

  @ViewChild("scrollContainer") set scrollContainer(element: ElementRef) {
    this.scrollIntoView(element.nativeElement);
  };

  @ViewChildren("weekDayItem") set yearDays(elements: QueryList<ElementRef>) {
    if (elements && !this.settings?.disableDaySelect) {
      for (let elementRef of elements) {
        const element: HTMLElement = elementRef.nativeElement as HTMLElement;

        this.ngZone.runOutsideAngular(() => {
          element.addEventListener("mouseover", (event: MouseEvent) => {

            if (this.mouseDown) {
              element.classList.toggle("cal--selected");
              this.mouseEnterEvent(event);
            }
          })
        })
      }
    }
  }

  protected DateUtil = DateUtil;
  protected Math = Math;

  protected dates: Date[] = [];
  protected today: Date;
  protected yesterday: Date;

  /**
   * Events that span multiple or one full day.
   */
  protected dayEvents: CalendarEvent[] = [];
  /**
   * Events, grouped per day of the week.
   */
  protected eventIndex: CalendarWeekEvent[][] = new Array(7);

  private mouseDown: boolean = false;

  //Ticker
  private currentTimePositionPercentage: number = 0;

  private _date: Date;

  private hasScrolledIntoView: boolean = false;

  private _tickerElement: HTMLElement;

  //#endregion

  constructor(private ngZone: NgZone) {
    this.today = new Date();

    this.yesterday = new Date();
    this.yesterday.setDate(this.today.getDate() - 1)
  }

  ngOnInit(): void {
    this.ticker();

    this.refreshEvents();
  }

  public refreshEvents(): void {
    this.eventIndex = this.getEvents();
  }

  //#region INTERNAL
  protected scrollIntoView(scrollContainer: Element) {
    if (!this.hasScrolledIntoView) {
      //As long as FireFox and other browsers don't support `container` we have to do this manually!
      //element.scrollIntoView({ block: 'center', container: 'nearest' } as any);

      scrollContainer.scroll(0, (scrollContainer.scrollHeight * (this.currentTimePositionPercentage / 100)) - (scrollContainer.clientHeight * 0.5));
    }

    this.hasScrolledIntoView = true;
    return '';
  }

  protected mouseDownEvent(event: MouseEvent) {
    this.mouseDown = true;
  }

  protected mouseUpEvent(event: MouseEvent) {
    this.mouseDown = false;
  }

  protected mouseEnterEvent(event: MouseEvent) {
    console.log("Mouse enter!")
  }

  private getDaysInWeek(date: Date): Date[] {
    const dates: Date[] = []

    const startOfWeek = new Date(date.getFullYear(), date.getMonth(), date.getDate() - ((date.getDay() || 7) - 1))

    for (let day = 0; day < 7; day += 1) {
      dates.push(new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + day));
    }

    return dates;
  }

  // #region Time ticker
  private ticker() {
    this.updateTimePositionPercentage();

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => { this.ticker() }, this.calculateMsToMinuteTick());
    })
  }

  private calculateMsToMinuteTick() {
    const now = new Date();
    const msUntilMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    return msUntilMinute;
  }

  private updateTimePositionPercentage() {
    const date = new Date();

    this.today = new Date();
    this.today.setHours(0, 0, 0, 0)

    const hour = date.getHours();
    const minute = date.getMinutes();

    if (!hour && !minute) {
      this.currentTimePositionPercentage = 0;
      return;
    }

    this.currentTimePositionPercentage = ((hour * 60 + minute) / (24 * 60)) * 100;

    if (this._tickerElement) {
      this._tickerElement.style.top = this.currentTimePositionPercentage + '%';
    }
  }

  // #endregion

  private getEvents(): CalendarWeekEvent[][] {
    if (!this.eventHandler)
      return;

    //Get events from consumer, then group them per day.
    const startDate = this.dates[0];
    const endDate = this.dates[this.dates.length - 1];

    const consumerEvents: CalendarEvent[] = this.eventHandler(startDate, endDate);

    let events: CalendarEvent[] = new Array(consumerEvents.length);

    //Put events that span multiple or full days into a seperate array.
    this.dayEvents = [];

    for (let i = 0; i < consumerEvents.length; i++) {
      const element = consumerEvents[i];

      if (element.startDate.getDate() != element.endDate.getDate() || ((element.endDate.getTime() - element.startDate.getTime()) >= 1439 * 60000)) {
        if (element.startDate < startDate || element.endDate > endDate)
          continue;

        this.dayEvents.push(element);
      } else {
        events[i] = element;
      }
    }

    let eventIndex: Array<CalendarEvent[]> = DayIndexUtil.buildIndex(startDate, endDate, events, (date: Date) => {
      return date.getDay();
    });

    let weekCollection: CalendarWeekEvent[][] = new Array(7);

    /**
     * A little explanation:
     * 
     * The week calendar is made up of 7 subgrids (7 days).
     * Inside this subgrid is a grid of events.
     * 
     * The grid of events are made up of: 288 rows and an automatically calculated amount of columns.
     * There are 24 hours in a day, so that means every row is equal to 5 minutes in a full day.
     * 
     * We can calculate the start of the row and the end row of an event by taking the start and end date of an event.
     * 
     * The CSS grid then will automatically place events next to each other, which is handy, HOWEVER, an event will NOT take up any remaining space automatically, there is no way to enable this in CSS.
     * Which means: we will have to calculate the `span` (how many columns an event uses) manually!!
     * 
     * Eg:
     * | Some event | Another event | Another event |
     * | Some event | Another event |   EMPTY COL   | <-- CSS doesn't automatically allow an event to grow (as of 2026).
     * 
     * This is what the code below does.
     * 
     * The code:
     * 1. Sorts from smallest to biggest event
     * 2. Divides events into "clusters", clusters are events which overlap each other.
     * 3. Calculates: 
     *    - Which column an event must be in.
     *    - How many columns an event must `span`.
     */

    //Loop through each day.
    for (let index = 0; index < eventIndex.length; index++) {
      const eventDay = eventIndex[index];

      if (!eventDay)
        continue;

      let events: CalendarWeekEvent[] =
        eventDay
          .sort((a, b) => (a.endDate.getTime() - a.startDate.getTime()) - (b.endDate.getTime() - b.startDate.getTime()))
          .map(e => ({
            gridRowSpan: -1,
            gridRowStart: -1,

            gridColumnStart: Math.round((e.startDate.getHours() * 12 + e.startDate.getMinutes() / 5 + 1)),
            gridColumnEnd: Math.round((e.endDate.getHours() * 12 + e.endDate.getMinutes() / 5 + 1)),
            event: e
          } as CalendarWeekEvent));

      let checkedEvents: Set<CalendarEvent> = new Set();

      let gridColumsMax: number = 0;

      const overlappingEventsClusters: CalendarWeekEvent[][] = events.map((e) => {

        if (checkedEvents.has(e.event))
          return;

        //Find overlapping events and map them.
        let overlapping = events.reduce(((accumulator, d) => {
          let eventOverlaps: boolean = DateUtil.dateOverlaps(e.event.startDate, e.event.endDate, d.event.startDate, d.event.endDate);

          if (eventOverlaps) {
            checkedEvents.add(d.event);
            accumulator.push(d);
          }

          return accumulator;
        }), [] as CalendarWeekEvent[]);

        gridColumsMax = Math.max(overlapping.length, gridColumsMax);

        return overlapping;
      })

      //Calculation for span and grid start/end.
      for (let cluster of overlappingEventsClusters) {

        if (!cluster)
          continue;

        //Get the last column an alread-calculated event is in.
        let maxCol = Math.max(0, (Math.max(...cluster.map(e => (e.gridRowStart - 1) + e.gridRowSpan))));

        //This gives us the area / columns we can divide between the non-calculated events.
        let area = gridColumsMax - maxCol;

        let nonCalculated = cluster.filter(e => e.gridRowSpan < 0);

        nonCalculated.forEach((e, i) => {
          e.gridRowStart = cluster.length - i;
          e.gridRowSpan = i == 0 ? Math.ceil(area / nonCalculated.length) : Math.floor(area / nonCalculated.length);
        })
      }

      weekCollection[index] = events.sort((a, b) => DateUtil.sortChronological(a.event.startDate, a.event.endDate, b.event.startDate, b.event.endDate));
    }

    return weekCollection;
  }
  // #endregion
}
